import { HTTPError } from "ky";
import { toast } from "sonner";
import * as z from "zod/v4";
import type { CSSProperties } from "react";
import { env } from "@/env";
import {
  setSessionStorageValue,
  takeSessionStorageValue,
} from "@/lib/hooks/sessionStorage";

const ZOOM_CONNECTION_REQUIRED_STATUS = 428;

const ZOOM_CONNECTION_REQUIRED_FALLBACK =
  "Zoom account not connected. Please connect your Zoom account to create a Zoom meeting bot.";

const ZOOM_OAUTH_RETURN_TO_KEY = "zoom-oauth-return-to";

/** Same-origin, query-less path, so a poisoned value cannot redirect off-site. */
const returnToSchema = z.string().regex(/^\/(?!\/)[^?#]*$/);

function splitLeadSentence(message: string): {
  title: string;
  description: string | null;
} {
  const trimmed = message.trim();
  const boundary = trimmed.indexOf(". ");
  if (boundary === -1) return { title: trimmed, description: null };
  return {
    title: trimmed.slice(0, boundary),
    description: trimmed.slice(boundary + 2).trim(),
  };
}

export function takeZoomOAuthReturnTo(): string | null {
  return takeSessionStorageValue(ZOOM_OAUTH_RETURN_TO_KEY, returnToSchema);
}

export function isZoomConnectionRequiredError(
  error: unknown,
): error is HTTPError {
  return (
    error instanceof HTTPError &&
    error.response.status === ZOOM_CONNECTION_REQUIRED_STATUS
  );
}

/**
 * Shows the 428 with a shortcut straight into Zoom's OAuth consent, and records
 * the origin page so the user can be returned to it.
 *
 * `onBeforeRedirect` runs just before leaving the SPA, for callers that need to
 * stash state they want back.
 */
export async function showZoomConnectionRequiredToast(
  error: HTTPError,
  onBeforeRedirect?: () => void,
) {
  let detail: string | null = null;
  try {
    const body = await error.response.json<{ detail?: unknown }>();
    if (typeof body.detail === "string") detail = body.detail;
  } catch {
    // ignore json parse errors
  }

  const { title, description } = splitLeadSentence(
    detail ?? ZOOM_CONNECTION_REQUIRED_FALLBACK,
  );

  toast.error(title, {
    description,
    duration: 10_000,
    style: { "--width": "440px" } as CSSProperties,
    classNames: {
      toast: "items-start gap-3 p-4!",
      icon: "mt-0.5 size-5",
      content: "gap-1",
      title: "text-[15px] leading-snug font-semibold",
      description: "text-sm leading-relaxed text-muted-foreground!",
      actionButton:
        "ml-auto h-9! shrink-0 self-center rounded-md px-3.5! text-sm! font-medium bg-[#DD2590]! text-white! hover:bg-[#C11574]!",
    },
    action: {
      label: "Connect Zoom",
      onClick: () => {
        onBeforeRedirect?.();
        setSessionStorageValue(
          ZOOM_OAUTH_RETURN_TO_KEY,
          window.location.pathname,
          returnToSchema,
        );
        window.location.assign(env.VITE_ZOOM_OAUTH_URL);
      },
    },
  });
}
